/** PersonPanelHost: mounts the person sheet while the URL has ?person=<id>. The URL is the state: Back closes it, a link opens it. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { pushedForSheet } from "../app/oneSheet";
import { useToast } from "../kit";
import { PersonSheet } from "./PersonSheet";
import { useReview } from "./useReview";

function parseId(raw: string | null): number | null {
  return raw !== null && /^\d+$/.test(raw) ? Number(raw) : null;
}

export function PersonPanelHost() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isReviewed, mark, undo } = useReview();

  const raw = params.get("person");
  const id = parseId(raw);
  // Keep the last person on screen while the sheet slides out.
  const [shown, setShown] = useState(id);
  if (id !== null && id !== shown) setShown(id);

  // One entry holds the sheet whoever is on it (app/oneSheet.ts): J and K replace it, keeping what it was.
  const withPerson = (next: number) => {
    const copy = new URLSearchParams(params);
    copy.set("person", String(next));
    setParams(copy, { replace: true, state: location.state });
  };

  /** A person that is not there: say so, and take it out of the address instead of leaving the sheet open on nothing. */
  const dropMissing = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("person");
        return next;
      },
      { replace: true },
    );
    toast.show({ message: "No such person" });
  }, [setParams, toast]);
  const invalid = raw !== null && id === null;
  const dropped = useRef<string | null>(null);
  useEffect(() => {
    if (!invalid || dropped.current === raw) return;
    dropped.current = raw;
    dropMissing();
  }, [invalid, raw, dropMissing]);

  // Esc, Close or a press on plain page: the entry added to open the sheet is undone, so Back never reopens it and no
  // Back is left that changes nothing. A sheet loaded from its own address has no such entry: its parameter goes in place.
  const close = () => {
    if (pushedForSheet(location.state)) {
      void navigate(-1);
      return;
    }
    const copy = new URLSearchParams(params);
    copy.delete("person");
    setParams(copy, { replace: true });
  };

  if (shown === null) return null;
  return <PersonSheet id={shown} open={id !== null} onNavigate={withPerson} onClose={close} onMissing={dropMissing} isReviewed={isReviewed} onReview={mark} onUndo={undo} />;
}
